import { useIsMutating, useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'

import { useAlertContext } from '@/contexts/AlertContext'
import { OFFER_AGGREGATE_BID_API_ID, offerAggregateBid } from '@/features/bidding/endpoints/aggregateBidOffer'
import type { AggregateBidOfferRequest } from '@/features/bidding/types/aggregateBidOffer'

function extractErrorMessage(error: unknown): string | null {
  if (!(error instanceof AxiosError) || error.response?.data === undefined) return null

  const data = error.response.data
  if (typeof data === 'string') return data
  if (typeof data === 'object' && data !== null && 'message' in data && typeof data.message === 'string') {
    return data.message
  }

  return null
}

export function useOfferAggregateBidMutation() {
  const { t } = useTranslation()
  const { pushAlert } = useAlertContext()

  const { mutateAsync, ...mutationResult } = useMutation({
    mutationFn: (request: AggregateBidOfferRequest) => offerAggregateBid(request),
    mutationKey: [OFFER_AGGREGATE_BID_API_ID],
    onError: (error) => {
      const apiMessage = extractErrorMessage(error)
      pushAlert({
        message: apiMessage ?? t('bidding.bidding_cascading.create_bids.submit_failed.text'),
        severity: 'error',
        title: t('bidding.bidding_cascading.create_bids.submit_failed.title'),
      })
    },
  })

  return {
    offerAggregateBid: mutateAsync,
    ...mutationResult,
  }
}

export const useIsOfferingAggregateBid = (): boolean => useIsMutating({ mutationKey: [OFFER_AGGREGATE_BID_API_ID] }) > 0
